import { Event } from '@interfaces/event';
import { CalendarEvent } from '@interfaces/calendar';
import { Announcement } from '@models/announcement';

export function evaluateTextWrapOverflow(numLinesPerEvent): number {
  if (numLinesPerEvent < 1) {
    // all items must take at least one line
    numLinesPerEvent = 1;
  } else {
    numLinesPerEvent = Math.ceil(numLinesPerEvent);
  }

  return numLinesPerEvent;
}

/**
 * Given an array of events (or announcements), character and row restrictions, determine
 * which items will make it to the calendar or not.
 */
export function getCalendarEventsIncludedExcluded(
  events: Array<Announcement | Event>,
  maxNumCharacter,
  maxNumRows,
) {
  let eventLists = {
    included: [],
    excluded: [],
  };
  let linesPerItem = [];
  let tempCount = maxNumRows;
  events.forEach((event) => {
    const details = getCalendarEventListing(event, maxNumCharacter);
    let counter = tempCount - details.numLinesPerItem;

    if (counter >= 0) {
      eventLists.included.push(event);
      tempCount = counter;
    } else {
      eventLists.excluded.push(event);
      tempCount = counter;
    }
    linesPerItem.push(details.numLinesPerItem);
  });

  return { sorted: eventLists, linesPerItem };
}

// given an event (or announcement) and a character limit, determine how many rows it should take
// optionally: return a text collection with superscript styles
export function getCalendarEventListing(
  item: Announcement | Event | CalendarEvent,
  maxNumCharacter: number,
  getCharCollection?: boolean,
): {
  label: string;
  numLinesPerItem: number;
  charCollection: Array<{ text: string; sup?: boolean }>;
} {
  let itemTitle;
  let charCollection = [];

  //@ts-ignore
  if (item.room) {
    const announcement = item as Announcement;

    if (getCharCollection && containsSymbolCharacter(announcement.title)) {
      charCollection = styleSymbols(announcement);
    }
    itemTitle = `${announcement.title}${
      announcement.room.shortName &&
      announcement.room.shortName !== ' - ' &&
      announcement.room.shortName !== '-'
        ? ', ' + announcement.room.shortName
        : ''
    }`;
  } else {
    let event;
    //@ts-ignore
    if (item?.original) {
      event = item as CalendarEvent;

      if (getCharCollection && containsSymbolCharacter(event.title)) {
        charCollection = styleSymbols(event.src);
      }

      if (event.locationShortName) {
        itemTitle = `${event.title}${
          event.locationShortName &&
          event.locationShortName !== ' - ' &&
          event.locationShortName !== '-'
            ? ', ' + event.locationShortName
            : ''
        }`;
      } else {
        itemTitle = `${event.title}${
          event.original?.room?.short &&
          event.original?.room?.short !== ' - ' &&
          event.original?.room?.short !== '-'
            ? ', ' + event.original.room.short
            : ''
        }`;
      }

      //@ts-ignore
    } else if (item?.fullEvent) {
      event = item as Event;

      if (getCharCollection && containsSymbolCharacter(event.title)) {
        charCollection = styleSymbols(event.fullEvent);
      }

      if (event.locationShortName) {
        itemTitle = `${event.title}${
          event.locationShortName &&
          event.locationShortName !== ' - ' &&
          event.locationShortName !== '-'
            ? ', ' + event.locationShortName
            : ''
        }`;
      } else {
        itemTitle = `${event.title}${
          event.fullEvent?.room?.shortName &&
          event.fullEvent?.room?.shortName !== ' - ' &&
          event.fullEvent?.room?.shortName !== '-'
            ? ', ' + event.fullEvent.room.shortName
            : ''
        }`;
      }
      itemTitle = `${event.title}${
        event.fullEvent?.room?.shortName &&
        event.fullEvent?.room?.shortName !== ' - ' &&
        event.fullEvent?.room?.shortName !== '-'
          ? ', ' + event.fullEvent.room.shortName
          : ''
      }`;
    }
  }

  let numLinesPerItem = evaluateTextWrapOverflow(
    itemTitle.length / maxNumCharacter,
  );

  if (itemTitle.length > maxNumCharacter) {
    const titleChunk = itemTitle.match(
      new RegExp(
        `\\b[\\w+\\s]{${maxNumCharacter - 4},${maxNumCharacter}}.?(?=\\s)|.+$`,
        'g',
      ),
    );
    const titleChunkNoWhitespace = titleChunk.map((line) => line.trim());
    itemTitle = titleChunkNoWhitespace.join('\n');
  }

  return { label: itemTitle, numLinesPerItem, charCollection };
}

function containsSymbolCharacter(text: string): boolean {
  if (text.includes('®') || text.includes('©') || text.includes('™') || text.includes('℠') || /\(SM\)/i.test(text)) {
    return true;
  }
  return false;
}

function styleSymbols(
  event: Event | Announcement,
): Array<{ text: string; sup?: boolean }> {
  const label = event.title.replace(/\(SM\)/gi, '℠');
  let splitString = [label];
  if (label.includes('®')) {
    splitString = splitString.flatMap((item) => item.split(/(®)/g));
  }
  if (label.includes('©')) {
    splitString = splitString.flatMap((item) => item.split(/(©)/g));
  }
  if (label.includes('™')) {
    splitString = splitString.flatMap((item) => item.split(/(™)/g));
  }
  if (label.includes('℠')) {
    splitString = splitString.flatMap((item) => item.split(/(℠)/g));
  }
  const charCollection = splitString
    .filter((label) => label.length !== 0)
    .map((label) => {
      if (label === '℠') {
        // FranklinGothic font does not fully render the ℠ Unicode character;
        // use 'SM' text in superscript position instead.
        return { text: 'SM', sup: true };
      } else if (containsSymbolCharacter(label)) {
        return {
          text: label,
          sup: true,
        };
      } else {
        return {
          text: label,
        };
      }
    });
  // @ts-ignore
  const room = event.room || event.locationShortName;
  if (room) {
    if (room.shortName && room.shortName !== ' - ' && room.shortName !== '-') {
      charCollection.push({
        text: `, ${room.shortName}`,
      });
    } else if (!room.shortName && room !== ' - ' && room !== '-') {
      charCollection.push({
        text: `, ${room}`,
      });
    }
  }
  return charCollection;
}
